import './index.scss';
import { lang } from '@umu/ukit-lang';
import { If } from 'common/src/components';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Iconfont, Table } from '@umu/umu-design';
import { defaultPermissionMemberList, getNavPermissionAccountListById } from '../../server';
import { IAccountInfo, MEMBER_TYPE } from '../../interface/account';
import { PaginationV2 } from '../../../pagination';
import { MemberDetailInfo } from '../memberInfo';
import { useInViewport } from 'ahooks';
import { Account } from '../../../../@utils/normalizeAccount';

interface IPermissionParams {
    obj_id: string;
    obj_type: string;
    page: number;
    size: number;
    se_id?: string;
}
interface IProps {
    id: string;
    seId?: string;
    sourceType: string;
    closeList?: IAccountInfo[];
    isModeView?: boolean;
    isReload?: boolean;
    normalizeAccountEPU?: (account: Account) => string;
    normalizeAccountName?: (account: Account) => string;
    onCancelPermission?: (record: IAccountInfo) => void;
    onCancelPermissionAll?: VoidFunction;
    onLoadData?: (params: IPermissionParams) => Promise<IList<IAccountInfo>>;
}

const getStaticText = (info: Record<string, number>) => {
    const { list_user_num = 0, list_department_num = 0, list_group_num = 0, list_class_num = 0 } = info;
    let result = '';
    if (list_user_num > 0) {
        result = `${lang('共{$0{0=人,1=人}}', list_user_num)}`;
    }
    if (list_department_num > 0) {
        result += !!result
            ? `,${lang('{$0{0=个部门,1=个部门}}', list_department_num)}`
            : `${lang('共{$0{0=个部门,1=个部门}}', list_department_num)}`;
    }
    if (list_group_num > 0) {
        result += !!result
            ? `,${lang('{$0{0=个分组,1=个分组}}', list_group_num)}`
            : `${lang('共{$0{0=个分组,1=个分组}}', list_group_num)}`;
    }
    if (list_class_num > 0) {
        result += !!result
            ? `,${lang('{$0{0=个班级,1=个班级}}', list_class_num)}`
            : `${lang('共{$0{0=个班级,1=个班级}}', list_class_num)}`;
    }
    return result;
};
const getStaticTotal = (info: Record<string, number>) => {
    const { list_user_num = 0, list_department_num = 0, list_group_num = 0, list_class_num = 0 } = info;
    let result = '';
    if (list_user_num > 0) {
        result = `${lang('已选择{$0{0=人,1=人}}', list_user_num)}`;
    }
    if (list_department_num > 0) {
        result += !!result
            ? `${lang('和')}${lang('{$0{0=个部门,1=个部门}}', list_department_num)}`
            : `${lang('已选择{$0{0=个部门,1=个部门}}', list_department_num)}`;
    }
    if (list_group_num > 0) {
        result += !!result
            ? `${lang('和')}${lang('{$0{0=个分组,1=个分组}}', list_group_num)}`
            : `${lang('已选择{$0{0=个分组,1=个分组}}', list_group_num)}`;
    }
    if (list_class_num > 0) {
        result += !!result
            ? `${lang('和')}${lang('{$0{0=个班级,1=个班级}}', list_class_num)}`
            : `${lang('已选择{$0{0=个班级,1=个班级}}', list_class_num)}`;
    }
    return result !== '' ? `${result}${lang('可见')}` : '';
};
const PermissionMemberList = (props: IProps) => {
    const {
        id,
        sourceType,
        seId,
        closeList = [],
        isModeView = false,
        isReload = false,
        onCancelPermission,
        onLoadData,
        normalizeAccountEPU,
        normalizeAccountName,
    } = props;
    const permissionRef = useRef<HTMLDivElement>(null);
    const [isHideList, setIsHideList] = useState(false);
    const [isShow, setIsShow] = useState(false);
    const [inViewport] = useInViewport(permissionRef);
    const refLoaded = useRef(false);
    const [dataInfo, setDataInfo] = useState<IList<IAccountInfo>>(defaultPermissionMemberList);
    const columns = [
        {
            title: (
                <div className="permission-member-rows">
                    <div className="permission-member-rows-title">{lang('可见人员')}</div>
                    <div className="permission-member-rows-sub">
                        {getStaticText(dataInfo.page_info as unknown as Record<string, number>)}
                    </div>
                </div>
            ),
            dataIndex: 'id',
            key: 'id',
            render: (_text: string, record: IAccountInfo) => {
                const isUser = record.account_type === MEMBER_TYPE.USER;
                return (
                    <div className="permission-member-rows">
                        <MemberDetailInfo
                            id={record.id}
                            className="use-table-permission-member"
                            type={record.account_type}
                            avatar={record.avatar}
                            userLevel={record?.user_level || ''}
                            name={
                                normalizeAccountName?.(record as unknown as Account) ||
                                record.account ||
                                record.name ||
                                record.login_name ||
                                ''
                            }
                            showUserLevel={record?.show_user_level || ''}
                            suffixElement={
                                isUser ? (
                                    normalizeAccountEPU ? (
                                        <div className="permission-member-desc">
                                            {normalizeAccountEPU(record as unknown as Account)}
                                        </div>
                                    ) : (
                                        ''
                                    )
                                ) : (
                                    <div className="permission-member-desc">
                                        {lang('共{$0{0=人,1=人}}', record?.user_num || 0)}
                                    </div>
                                )
                            }
                        />
                        <If condition={!isModeView}>
                            <Button type="text" isOnlyChildren onClick={() => onCancelPermission?.(record)}>
                                {lang('取消可见')}
                            </Button>
                        </If>
                    </div>
                );
            },
        },
    ];

    const onPageChange = (page = 1) => {
        const params: IPermissionParams = {
            obj_id: id,
            obj_type: sourceType,
            page,
            size: 20,
        };
        if (seId) {
            params.se_id = seId;
        }
        const loadFunc = onLoadData || getNavPermissionAccountListById;
        loadFunc(params).then((res: IList<IAccountInfo>) => {
            setDataInfo(res);
            refLoaded.current = true;
        });
    };
    useEffect(() => {
        if (!refLoaded.current) {
            onPageChange();
        }
    }, [inViewport]);
    useEffect(() => {
        if (isReload) {
            onPageChange();
        }
    }, [isReload]);
    const onCancelPermissionAll = () => {
        setIsHideList(true);
        props?.onCancelPermissionAll?.();
    };
    const list = (dataInfo.list || []).filter(
        (item) =>
            !closeList.find((closeItem) => closeItem.id === item.id && closeItem.account_type === item.account_type),
    );
    return (
        <div className="permission-member" style={{ display: !isHideList ? '' : 'none' }} ref={permissionRef}>
            <If condition={dataInfo.page_info.list_total_num > 0}>
                <div className="permission-member-heard">
                    <span>{getStaticTotal(dataInfo.page_info as unknown as Record<string, number>)}</span>
                    <Iconfont
                        type="RadiusTriangle"
                        className={`permission-member-heard-icon ${
                            isShow ? 'permission-member-heard-icon-active' : ''
                        }`}
                        onClick={() => setIsShow(!isShow)}
                    />
                    <If condition={!isModeView}>
                        <Button type="text" isOnlyChildren onClick={onCancelPermissionAll}>
                            {lang('全部取消可见')}
                        </Button>
                    </If>
                </div>
            </If>
            <div
                style={{
                    display: isShow ? '' : 'none',
                    marginTop: dataInfo.page_info.list_total_num > 0 ? '0.12rem' : '0',
                }}
            >
                <Table
                    columns={columns}
                    data={list}
                    emptyText={
                        dataInfo.page_info.total_page_num > 1 && list.length === 0
                            ? lang('本页已清空')
                            : lang('暂无数据')
                    }
                    scroll={{ y: 240 }}
                />
                <If condition={dataInfo.page_info.total_page_num > 1}>
                    <div className="permission-member-pagination">
                        <PaginationV2
                            currentPage={dataInfo.page_info.current_page}
                            maxPage={dataInfo.page_info.total_page_num}
                            showSizeChanger
                            forceShow={true}
                            onPageChange={onPageChange}
                        />
                    </div>
                </If>
            </div>
        </div>
    );
};
export { PermissionMemberList };
